// Substep 5-ξ.3: Medical records extraction + review.
//
// Sits on the Providers tab, next to the records-status card. Three
// surfaces in one:
//
//   Upload      drop a records PDF, get encounters back
//   Review      verify each encounter against its source page —
//               nothing is demand-ready until a human has checked it
//   Timeline    the chronology, billed total, and treatment gaps a
//               demand package is built from
//
// The review step is the point. Extraction is a first pass, not an
// answer: a wrong date of service or a missed surgery charge changes
// a demand, so every row shows its source page and stays unverified
// until someone confirms it.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const TYPE_LABELS = {
  office_visit: "Office visit", emergency: "ER", imaging: "Imaging",
  physical_therapy: "PT", surgery: "Surgery", specialist: "Specialist",
  lab: "Lab", other: "Other",
};

const _money = (v) => v == null ? "—" :
  "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const _date = (v) => v ? v.slice(0, 10) : "—";

export function MedicalRecordsCard({ matterId, showNotification }) {
  const [extractions, setExtractions] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [tab, setTab] = useState("timeline");
  const [editing, setEditing] = useState(null);   // encounter being corrected
  const [busyId, setBusyId] = useState(null);
  const [bulkVerifying, setBulkVerifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ex, tl, rd] = await Promise.all([
        apiFetch(`/api/matters/${matterId}/medical-records`),
        apiFetch(`/api/matters/${matterId}/treatment-timeline`),
        apiFetch(`/api/matters/${matterId}/demand-readiness`),
      ]);
      setExtractions(ex.data || []);
      setTimeline(tl);
      setReadiness(rd);
    } catch (e) {
      showNotification?.(`Failed to load records: ${e.message}`, "error");
      setExtractions([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      showNotification?.("Medical records must be a PDF.", "warning");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/matters/${matterId}/medical-records/upload`, {
        method: "POST", body: fd, credentials: "same-origin",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      if (data.reused) {
        showNotification?.(
          "These exact records were already processed — showing the existing extraction.",
          "success");
      } else if (data.status === "failed") {
        showNotification?.(`Extraction failed: ${data.error || "no encounters found"}`, "error");
      } else {
        const bits = [`${data.encounter_count} encounters`];
        if (data.pages_ocr) bits.push(`${data.pages_ocr} scanned pages`);
        if (data.truncated) bits.push(`only first ${data.pages_processed} of ${data.page_count} pages`);
        showNotification?.(`Extracted ${bits.join(" · ")}.`,
          data.truncated ? "warning" : "success");
      }
      await load();
      setTab("review");
    } catch (e) {
      showNotification?.(`Upload failed: ${e.message}`, "error");
    }
    setUploading(false);
  };

  // Refresh the derived panels without blocking the row that was just
  // clicked. Readiness genuinely needs the server (blockers are
  // computed there), but nobody should watch a spinner for it.
  const refreshDerived = useCallback(async () => {
    try {
      const [tl, rd] = await Promise.all([
        apiFetch(`/api/matters/${matterId}/treatment-timeline`),
        apiFetch(`/api/matters/${matterId}/demand-readiness`),
      ]);
      setTimeline(tl);
      setReadiness(rd);
    } catch { /* the optimistic view stands until the next load */ }
  }, [matterId]);

  const verify = async (enc, { exclude = false, corrections = null } = {}) => {
    // Optimistic. The old flow awaited the PATCH and then a full
    // reload — four sequential round trips, one of which
    // (demand-readiness) recomputes the timeline server-side — before
    // the row moved. Verifying eight encounters meant eight of those,
    // which is most of the work this feature was supposed to save.
    const prev = timeline?.encounters || [];
    const applyLocal = (list) => list.map(e =>
      e.id === enc.id
        ? { ...e, ...(corrections || {}), is_verified: !exclude, _excluded: exclude }
        : e);

    setTimeline(t => t ? {
      ...t,
      encounters: applyLocal(t.encounters || []).filter(e => !e._excluded),
      verified_count: (t.verified_count || 0) + (exclude ? 0 : 1),
    } : t);
    setEditing(null);

    try {
      await apiFetch(`/api/medical-encounters/${enc.id}/verify`, {
        method: "POST", body: JSON.stringify({ exclude, corrections }),
      });
      refreshDerived();
    } catch (e) {
      // Put it back the way it was — the server is the truth.
      setTimeline(t => t ? { ...t, encounters: prev } : t);
      showNotification?.(`Failed: ${e.message}`, "error");
      load();
    }
  };

  const verifyAllRemaining = async () => {
    const pending = (timeline?.encounters || []).filter(e => !e.is_verified);
    if (!pending.length) return;
    if (!window.confirm(
      `Confirm all ${pending.length} remaining encounter${pending.length === 1 ? "" : "s"}?\n\n` +
      `Only do this if you've checked them against the source pages — ` +
      `confirming is what tells the demand composer these are accurate.`
    )) return;

    setBulkVerifying(true);
    // Optimistic for the whole set, then fire the calls concurrently.
    setTimeline(t => t ? {
      ...t,
      encounters: (t.encounters || []).map(e => ({ ...e, is_verified: true })),
      verified_count: (t.encounters || []).length,
    } : t);

    const results = await Promise.allSettled(pending.map(e =>
      apiFetch(`/api/medical-encounters/${e.id}/verify`, {
        method: "POST", body: JSON.stringify({ exclude: false, corrections: null }),
      })
    ));
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) {
      showNotification?.(
        `${failed} of ${pending.length} could not be confirmed — reloading.`, "error");
      await load();
    } else {
      showNotification?.(
        `Confirmed ${pending.length} encounter${pending.length === 1 ? "" : "s"}.`, "success");
      refreshDerived();
    }
    setBulkVerifying(false);
  };

  const removeExtraction = async (ex) => {
    if (!window.confirm(`Remove "${ex.source_filename}" and its ${ex.encounter_count} encounters?`)) return;
    try {
      await apiFetch(`/api/medical-records/${ex.id}`, { method: "DELETE" });
      showNotification?.("Extraction removed.", "success");
      await load();
    } catch (e) { showNotification?.(`Failed: ${e.message}`, "error"); }
  };

  const cardStyle = {
    background: "#fff", borderRadius: "12px", padding: "20px 24px",
    border: `1px solid ${C.warmGray}`, marginBottom: "20px",
  };

  if (extractions === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement(Spinner, null));
  }

  const unverified = timeline
    ? timeline.encounter_count - timeline.verified_count : 0;

  return React.createElement("div", { style: cardStyle },
    // Header
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between",
               alignItems: "center", marginBottom: "12px", gap: "10px", flexWrap: "wrap" },
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
                   letterSpacing: "0.08em", fontWeight: 600, margin: 0 },
        }, "Medical Records"),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textMuted, marginTop: "3px" },
        }, "Upload a records PDF to extract the treatment chronology and billed total."),
      ),
      React.createElement("label", {
        style: {
          padding: "6px 14px", borderRadius: "8px", border: "none",
          background: uploading ? C.warmGray : C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 600,
          cursor: uploading ? "default" : "pointer",
        },
      },
        uploading ? "Extracting…" : "Upload records",
        React.createElement("input", {
          type: "file", accept: ".pdf", disabled: uploading,
          style: { display: "none" },
          onChange: e => { upload(e.target.files?.[0]); e.target.value = ""; },
        }),
      ),
    ),

    uploading && React.createElement("div", {
      style: { padding: "10px 14px", background: `${C.gold}12`, borderRadius: "8px",
               fontSize: "12px", color: C.textDark, marginBottom: "12px" },
    }, "Reading the document — a large or scanned set can take a minute."),

    // Empty state doubles as a drop zone
    extractions.length === 0 && !uploading && React.createElement("div", {
      onDragOver: e => { e.preventDefault(); setDragOver(true); },
      onDragLeave: () => setDragOver(false),
      onDrop: e => {
        e.preventDefault(); setDragOver(false);
        upload(e.dataTransfer.files?.[0]);
      },
      style: {
        padding: "30px", textAlign: "center", borderRadius: "10px",
        border: `1px dashed ${dragOver ? C.gold : C.warmGray}`,
        background: dragOver ? `${C.gold}0c` : C.warmWhite,
        color: C.textMuted, fontSize: "13px",
      },
    }, "Drop a records PDF here, or use Upload records above."),

    extractions.length > 0 && React.createElement(React.Fragment, null,
      // Readiness banner — the "can I demand on this yet" answer
      readiness && React.createElement("div", {
        style: {
          padding: "11px 14px", borderRadius: "8px", marginBottom: "12px",
          background: readiness.ready ? C.successBg : `${C.gold}12`,
          border: `1px solid ${readiness.ready ? C.success : C.gold}40`,
        },
      },
        React.createElement("div", {
          style: { fontSize: "12px", fontWeight: 700,
                   color: readiness.ready ? C.success : C.textDark },
        }, readiness.ready
            ? "Records are demand-ready"
            : `Not demand-ready — ${readiness.blockers.length} blocker${readiness.blockers.length !== 1 ? "s" : ""}`),

        // Blockers and warnings are labelled separately. Colour alone
        // was too subtle: a "1 blocker" headline above three
        // undifferentiated bullets reads as three problems, and the
        // two categories mean genuinely different things — one stops
        // a demand, the other is a fact about the case to be ready for.
        (readiness.blockers || []).length > 0 && React.createElement("div",
          { style: { marginTop: "7px" } },
          React.createElement("div", {
            style: { fontSize: "9px", fontWeight: 700, color: C.error,
                     letterSpacing: "0.06em", textTransform: "uppercase" },
          }, "Blocking"),
          ...(readiness.blockers || []).map((b, i) => React.createElement("div", {
            key: "b" + i,
            style: { fontSize: "11px", color: C.error, marginTop: "2px" },
          }, "• " + b)),
        ),
        (readiness.warnings || []).length > 0 && React.createElement("div",
          { style: { marginTop: "8px" } },
          React.createElement("div", {
            style: { fontSize: "9px", fontWeight: 700, color: C.textMuted,
                     letterSpacing: "0.06em", textTransform: "uppercase" },
          }, readiness.ready ? "Worth knowing before you send" : "Worth knowing"),
          ...(readiness.warnings || []).map((w, i) => React.createElement("div", {
            key: "w" + i,
            style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" },
          }, "• " + w)),
        ),
      ),

      // Summary tiles
      timeline && React.createElement("div", {
        style: { display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" },
      },
        ...[
          ["Encounters", timeline.encounter_count],
          ["Verified", `${timeline.verified_count} / ${timeline.encounter_count}`],
          ["Providers", timeline.providers.length],
          ["Billed", _money(timeline.total_charges)],
          ["Treatment span", timeline.treatment_days ? `${timeline.treatment_days}d` : "—"],
          ["Gaps", timeline.gaps.length],
        ].map(([label, val]) => React.createElement("div", {
          key: label,
          style: { flex: "1 1 100px", background: C.warmWhite, borderRadius: "8px",
                   padding: "8px 12px" },
        },
          React.createElement("div", {
            style: { fontSize: "9px", color: C.textMuted, fontWeight: 700,
                     letterSpacing: "0.05em", textTransform: "uppercase" },
          }, label),
          React.createElement("div", {
            style: { fontSize: "16px", fontWeight: 700, color: C.navy, marginTop: "2px" },
          }, String(val)),
        )),
      ),

      // Tabs
      React.createElement("div", {
        style: { display: "flex", gap: "4px", borderBottom: `1px solid ${C.warmGray}`,
                 marginBottom: "12px" },
      },
        // "Uploaded files", not "Documents" — the matter already has a
        // Documents TAB, and naming this the same thing sent the first
        // person who used it to the wrong screen.
        ...[["timeline", "Timeline"],
            ["review", unverified ? `Review (${unverified})` : "Review"],
            ["sources", `Uploaded files (${extractions.length})`]]
          .map(([id, label]) => React.createElement("button", {
            key: id, onClick: () => setTab(id),
            style: {
              padding: "7px 14px", border: "none", background: "transparent",
              borderBottom: tab === id ? `2px solid ${C.gold}` : "2px solid transparent",
              color: tab === id ? C.navy : C.textMuted,
              fontWeight: tab === id ? 700 : 500, fontSize: "12px", cursor: "pointer",
            },
          }, label)),
      ),

      tab === "timeline" && _renderTimeline(timeline),
      tab === "review" && _renderReview(timeline, {
        busyId, editing, setEditing, verify,
        verifyAllRemaining, bulkVerifying,
      }),
      tab === "sources" && _renderSources(extractions, removeExtraction),
    ),
  );
}


function _renderTimeline(timeline) {
  if (!timeline || !timeline.encounters.length) {
    return React.createElement("div", {
      style: { padding: "20px", textAlign: "center", color: C.textMuted, fontSize: "12px" },
    }, "No encounters extracted yet.");
  }
  const gapAfter = {};
  (timeline.gaps || []).forEach(g => { gapAfter[g.after] = g; });

  return React.createElement("div", null,
    ...timeline.encounters.map((e, i) => React.createElement(React.Fragment, { key: e.id },
      React.createElement("div", {
        style: {
          display: "flex", gap: "12px", padding: "9px 0",
          borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}60`,
          opacity: e.is_verified ? 1 : 0.75,
        },
      },
        React.createElement("div", {
          style: { minWidth: "84px", fontSize: "12px", fontWeight: 600,
                   color: C.navy, fontVariantNumeric: "tabular-nums" },
        }, _date(e.date_of_service)),
        React.createElement("div", { style: { flex: 1, minWidth: 0 } },
          React.createElement("div", {
            style: { display: "flex", gap: "7px", alignItems: "center", flexWrap: "wrap" },
          },
            React.createElement("span", { style: { fontSize: "12px", fontWeight: 600, color: C.textDark } },
              e.provider_name || "Unknown provider"),
            React.createElement("span", {
              style: { fontSize: "9px", padding: "1px 6px", borderRadius: "3px",
                       background: `${C.navy}10`, color: C.navy, fontWeight: 600 },
            }, TYPE_LABELS[e.encounter_type] || e.encounter_type),
            !e.is_verified && React.createElement("span", {
              style: { fontSize: "9px", color: C.gold, fontWeight: 700 },
            }, "UNVERIFIED"),
          ),
          e.chief_complaint && React.createElement("div", {
            style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" },
          }, e.chief_complaint),
          (e.diagnoses || []).length > 0 && React.createElement("div", {
            style: { fontSize: "10px", color: C.textMuted, marginTop: "2px" },
          }, e.diagnoses.map(d => [d.code, d.description].filter(Boolean).join(" ")).join(" · ")),
        ),
        React.createElement("div", {
          style: { fontSize: "12px", fontWeight: 600, color: e.charges ? C.navy : C.textLight,
                   fontVariantNumeric: "tabular-nums", minWidth: "80px", textAlign: "right" },
        }, _money(e.charges)),
      ),
      gapAfter[e.date_of_service] && React.createElement("div", {
        key: "gap" + e.id,
        style: {
          margin: "4px 0 4px 96px", padding: "5px 10px", borderRadius: "6px",
          background: `${C.gold}14`, fontSize: "11px", color: C.textDark,
        },
      }, `${gapAfter[e.date_of_service].days}-day gap in treatment — a defense adjuster will raise this`),
    )),
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", paddingTop: "10px",
               marginTop: "6px", borderTop: `2px solid ${C.warmGray}`,
               fontSize: "13px", fontWeight: 700, color: C.navy },
    },
      React.createElement("span", null, "Total billed"),
      React.createElement("span", { style: { fontVariantNumeric: "tabular-nums" } },
        _money(timeline.total_charges)),
    ),
    timeline.encounters_missing_charges > 0 && React.createElement("div", {
      style: { fontSize: "10px", color: C.gold, textAlign: "right", marginTop: "3px" },
    }, `${timeline.encounters_missing_charges} encounter(s) had no billed amount in the records`),
  );
}


function _renderReview(timeline, { busyId, editing, setEditing, verify,
                                   verifyAllRemaining, bulkVerifying }) {
  const pending = (timeline?.encounters || []).filter(e => !e.is_verified);
  if (!pending.length) {
    return React.createElement("div", {
      style: { padding: "20px", textAlign: "center", color: C.success, fontSize: "12px" },
    }, "Every encounter has been verified against the source records.");
  }
  return React.createElement("div", null,
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between",
               alignItems: "flex-start", gap: "12px", marginBottom: "10px" },
    },
      React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, flex: 1 },
      }, "Check each against the page it came from. Correct anything the extraction got wrong, or exclude a row that isn't a real encounter."),
      // Bulk confirm. Clicking through a dozen encounters one at a
      // time is its own tax on the paralegal this feature is meant to
      // save time for — but the confirm dialog is deliberate, because
      // confirming is the signal the demand composer trusts.
      pending.length > 1 && React.createElement("button", {
        onClick: verifyAllRemaining,
        disabled: bulkVerifying,
        style: {
          padding: "5px 12px", borderRadius: "6px",
          border: `1px solid ${C.warmGray}`, background: "#fff",
          color: C.textDark, fontSize: "11px", cursor: "pointer",
          flexShrink: 0, whiteSpace: "nowrap",
        },
      }, bulkVerifying ? "Confirming…" : `Confirm all ${pending.length}`),
    ),
    ...pending.map((e, i) => editing?.id === e.id
      ? _renderEditor(e, editing, setEditing, verify, busyId)
      : React.createElement("div", {
          key: e.id,
          style: {
            padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}60`,
            display: "flex", gap: "10px", alignItems: "flex-start",
          },
        },
          React.createElement("div", { style: { flex: 1, minWidth: 0 } },
            React.createElement("div", {
              style: { display: "flex", gap: "7px", alignItems: "center", flexWrap: "wrap" },
            },
              React.createElement("span", {
                style: { fontSize: "12px", fontWeight: 700, color: C.navy,
                         fontVariantNumeric: "tabular-nums" },
              }, _date(e.date_of_service)),
              React.createElement("span", { style: { fontSize: "12px", color: C.textDark } },
                e.provider_name || "Unknown provider"),
              React.createElement("span", {
                style: { fontSize: "9px", padding: "1px 6px", borderRadius: "3px",
                         background: `${C.navy}10`, color: C.navy, fontWeight: 600 },
              }, TYPE_LABELS[e.encounter_type] || e.encounter_type),
              e.confidence === "low" && React.createElement("span", {
                style: { fontSize: "9px", padding: "1px 6px", borderRadius: "3px",
                         background: C.errorBg, color: C.error, fontWeight: 700 },
              }, "LOW CONFIDENCE"),
              e.source_page && React.createElement("span", {
                style: { fontSize: "10px", color: C.textMuted },
                title: "Check this page in the source PDF",
              }, `page ${e.source_page}`),
            ),
            e.chief_complaint && React.createElement("div", {
              style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" },
            }, e.chief_complaint),
            React.createElement("div", {
              style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" },
            }, "Billed: ", _money(e.charges)),
          ),
          React.createElement("div", { style: { display: "flex", gap: "5px", flexShrink: 0 } },
            React.createElement("button", {
              onClick: () => verify(e), disabled: busyId === e.id,
              style: { padding: "4px 12px", borderRadius: "6px", border: "none",
                       background: C.gold, color: "#fff", fontSize: "11px",
                       fontWeight: 600, cursor: "pointer" },
            }, busyId === e.id ? "…" : "Confirm"),
            React.createElement("button", {
              onClick: () => setEditing({ id: e.id, ...e }),
              style: { padding: "4px 10px", borderRadius: "6px",
                       border: `1px solid ${C.warmGray}`, background: "#fff",
                       color: C.textDark, fontSize: "11px", cursor: "pointer" },
            }, "Fix"),
            React.createElement("button", {
              onClick: () => verify(e, { exclude: true }), disabled: busyId === e.id,
              title: "Not a real encounter — exclude from the timeline",
              style: { padding: "4px 10px", borderRadius: "6px",
                       border: `1px solid ${C.warmGray}`, background: "#fff",
                       color: C.error, fontSize: "11px", cursor: "pointer" },
            }, "Exclude"),
          ),
        )),
  );
}


function _renderEditor(e, editing, setEditing, verify, busyId) {
  const set = (k) => (ev) => setEditing(d => ({ ...d, [k]: ev.target.value }));
  const field = (label, key, type = "text") => React.createElement("div", null,
    React.createElement("label", {
      style: { display: "block", fontSize: "10px", fontWeight: 600,
               color: C.navy, marginBottom: "2px" },
    }, label),
    React.createElement("input", {
      type, value: editing[key] ?? "", onChange: set(key),
      style: { width: "100%", padding: "6px 9px", borderRadius: "6px",
               border: `1px solid ${C.warmGray}`, fontSize: "12px",
               boxSizing: "border-box" },
    }),
  );
  return React.createElement("div", {
    key: e.id,
    style: { padding: "12px", background: C.warmWhite, borderRadius: "8px",
             marginBottom: "8px", border: `1px solid ${C.gold}55` },
  },
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "120px 1fr 110px", gap: "8px", marginBottom: "8px" },
    },
      field("Date of service", "date_of_service"),
      field("Provider", "provider_name"),
      field("Charges", "charges"),
    ),
    field("Chief complaint", "chief_complaint"),
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" },
    },
      React.createElement("span", { style: { fontSize: "10px", color: C.textMuted } },
        e.source_page ? `Source: page ${e.source_page}` : ""),
      React.createElement("div", { style: { display: "flex", gap: "6px" } },
        React.createElement("button", {
          onClick: () => setEditing(null),
          style: { padding: "5px 12px", borderRadius: "6px",
                   border: `1px solid ${C.warmGray}`, background: "#fff",
                   color: C.textMuted, fontSize: "11px", cursor: "pointer" },
        }, "Cancel"),
        React.createElement("button", {
          onClick: () => verify(e, {
            corrections: {
              date_of_service: editing.date_of_service,
              provider_name: editing.provider_name,
              charges: editing.charges,
              chief_complaint: editing.chief_complaint,
            },
          }),
          disabled: busyId === e.id,
          style: { padding: "5px 14px", borderRadius: "6px", border: "none",
                   background: C.gold, color: "#fff", fontSize: "11px",
                   fontWeight: 600, cursor: "pointer" },
        }, busyId === e.id ? "…" : "Save + confirm"),
      ),
    ),
  );
}


function _renderSources(extractions, removeExtraction) {
  return React.createElement("div", null,
    ...extractions.map((ex, i) => React.createElement("div", {
      key: ex.id,
      style: { padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}60`,
               display: "flex", gap: "10px", alignItems: "flex-start" },
    },
      React.createElement("div", { style: { flex: 1, minWidth: 0 } },
        React.createElement("div", {
          style: { display: "flex", gap: "7px", alignItems: "center", flexWrap: "wrap" },
        },
          React.createElement("span", { style: { fontSize: "12px", fontWeight: 600, color: C.navy } },
            ex.source_filename || "(unnamed)"),
          ex.status === "needs_review" && React.createElement("span", {
            style: { fontSize: "9px", padding: "1px 6px", borderRadius: "999px",
                     background: `${C.gold}22`, color: C.textDark, fontWeight: 700 },
          }, "NEEDS REVIEW"),
          ex.status === "failed" && React.createElement("span", {
            style: { fontSize: "9px", padding: "1px 6px", borderRadius: "999px",
                     background: C.errorBg, color: C.error, fontWeight: 700 },
          }, "FAILED"),
          ex.truncated && React.createElement("span", {
            style: { fontSize: "9px", padding: "1px 6px", borderRadius: "999px",
                     background: C.errorBg, color: C.error, fontWeight: 700 },
            title: "Not all pages were processed — encounters may be missing",
          }, "TRUNCATED"),
        ),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textMuted, marginTop: "2px",
                   display: "flex", gap: "10px", flexWrap: "wrap" },
        },
          ex.provider_name && React.createElement("span", null, ex.provider_name),
          React.createElement("span", null, `${ex.encounter_count} encounters`),
          ex.page_count && React.createElement("span", null,
            `${ex.pages_processed}/${ex.page_count} pages`),
          ex.pages_ocr > 0 && React.createElement("span", {
            title: "Pages with no text layer that needed image reading",
          }, `${ex.pages_ocr} scanned`),
          ex.total_charges != null && React.createElement("span", null, _money(ex.total_charges)),
        ),
        ex.error && React.createElement("div", {
          style: { fontSize: "10px", color: C.error, marginTop: "2px" },
        }, ex.error),
      ),
      React.createElement("button", {
        onClick: () => removeExtraction(ex),
        style: { padding: "3px 10px", borderRadius: "6px",
                 border: `1px solid ${C.warmGray}`, background: "#fff",
                 color: C.error, fontSize: "11px", cursor: "pointer", flexShrink: 0 },
      }, "Remove"),
    )),
  );
}
